import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardVisibility } from '@catalyst-psa/shared';
import { DASHBOARD_VISIBILITY_LABELS, levelSatisfies, menuLevelOf } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { mutationError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Modal } from '../../components/ui/Modal.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { formatDateTime } from '../../utils/date.js';
import { useCreateDashboard, useDashboards, useMemberPreferences } from './api.js';

/** True when the member may create or edit SHARED dashboards. */
export function useCanManageShared(): boolean {
  const { principal, isAdmin } = useAuth();
  if (isAdmin) return true;
  return levelSatisfies(menuLevelOf(principal?.menuPermissions ?? {}, 'dashboards'), 'edit');
}

export function DashboardsPage(): JSX.Element {
  const navigate = useNavigate();
  const { principal } = useAuth();
  const query = useDashboards();
  const preferences = useMemberPreferences();
  const pinnedId = preferences.data?.defaultDashboardId ?? null;
  const canManageShared = useCanManageShared();
  const [createOpen, setCreateOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const currentMemberId = principal?.type === 'member' ? principal.id : null;
  const all = query.data ?? [];
  // Admins see everyone's personal dashboards; keep the list to your own unless asked.
  const othersCount = all.filter(
    (d) => d.visibility === 'personal' && d.ownerId !== currentMemberId,
  ).length;
  const dashboards = showAll
    ? all
    : all.filter((d) => d.visibility !== 'personal' || d.ownerId === currentMemberId);

  return (
    <div>
      <PageHeader
        title="Dashboards"
        description="Build your own views of PSA data from cards you choose."
        actions={
          <div className="flex items-center gap-3">
            {othersCount > 0 ? (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Show all ({othersCount})
              </label>
            ) : null}
            <Button onClick={() => setCreateOpen(true)}>New dashboard</Button>
          </div>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Visibility</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">Cards</th>
              <th className="px-4 py-3 font-medium">Last updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={5}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={dashboards.length === 0}
              emptyTitle="No dashboards yet"
              emptyDescription="Create a dashboard and add cards to track the work that matters to you."
              emptyAction={<Button onClick={() => setCreateOpen(true)}>New dashboard</Button>}
            >
              {dashboards.map((dashboard) => (
                <tr
                  key={dashboard.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/dashboards/${dashboard.id}`)}
                >
                  <td className="px-4 py-3">
                    <p className="flex items-center gap-2 font-medium text-slate-900">
                      {dashboard.name}
                      {dashboard.id === pinnedId ? (
                        <Badge tone="amber">My home page</Badge>
                      ) : null}
                    </p>
                    {dashboard.description ? (
                      <p className="text-xs text-slate-500">{dashboard.description}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={dashboard.visibility === 'shared' ? 'blue' : 'gray'}>
                      {DASHBOARD_VISIBILITY_LABELS[dashboard.visibility]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{dashboard.ownerName ?? '—'}</td>
                  <td className="px-4 py-3 tabular-nums text-slate-600">{dashboard.cardCount}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateTime(dashboard.updatedAt)}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>

      <CreateDashboardModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        canManageShared={canManageShared}
      />
    </div>
  );
}

function CreateDashboardModal({
  isOpen,
  onClose,
  canManageShared,
}: {
  isOpen: boolean;
  onClose: () => void;
  canManageShared: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const createDashboard = useCreateDashboard();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<DashboardVisibility>('personal');
  const reset = createDashboard.reset;

  useEffect(() => {
    if (!isOpen) return;
    reset();
    setName('');
    setDescription('');
    setVisibility('personal');
  }, [isOpen, reset]);

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!name.trim()) return;
    createDashboard.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        visibility,
      },
      {
        onSuccess: (created) => {
          onClose();
          navigate(`/dashboards/${created.id}`);
        },
      },
    );
  };

  const error = mutationError(createDashboard.error, 'Could not create the dashboard.');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New dashboard"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-dashboard"
            disabled={!name.trim()}
            isLoading={createDashboard.isPending}
          >
            Create
          </Button>
        </div>
      }
    >
      <form id="new-dashboard" onSubmit={handleSubmit} className="space-y-4">
        {error ? (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        ) : null}
        <TextField
          label="Name"
          required
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Delivery health"
        />
        <TextAreaField
          label="Description"
          rows={2}
          value={description}
          maxLength={1000}
          onChange={(e) => setDescription(e.target.value)}
        />
        <SelectField
          label="Visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as DashboardVisibility)}
          options={[
            { value: 'personal', label: 'Personal — only I can see it' },
            ...(canManageShared
              ? [{ value: 'shared', label: 'Shared — everyone with dashboard access' }]
              : []),
          ]}
        />
        {!canManageShared ? (
          <p className="text-xs text-slate-500">
            You need edit access to Dashboards to create a shared dashboard.
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
